"use strict";

/**
 * WEB-3455 — Homesuite Updates per-project back-office (CMS category 90).
 *
 * Lists, creates, edits, and deletes Update rows that surface on the
 * Homesuite Flutter app's Updates screen. All endpoints are scoped to
 * a single property via /sec/developer/properties/{propertyId}/homesuite-updates.
 *
 * The page lives under a specific property URL, so the property is
 * resolved server-side from the URL — there is no project picker on
 * the modal.
 *
 * UI controls are gated client-side via the four
 * DMS_HOMESUITE_UPDATES_{VIEW,ADD,EDIT,DELETE} permissions; the server
 * re-checks each on every request, so the client check is purely UX.
 */
class PropertyKeyHandoverUpdatesController {

    constructor($scope, $timeout, SSHttpService, SSAlertService, SSAppService,
                SSUserService, SSUtilService, SSDateService, SSConfirmationService,
                SSAwsService) {
        const self = this;

        self.$scope = $scope;
        self.$timeout = $timeout;
        self.SSHttpService = SSHttpService;
        self.SSAlertService = SSAlertService;
        self.SSAppService = SSAppService;
        self.SSUserService = SSUserService;
        self.SSUtilService = SSUtilService;
        self.SSDateService = SSDateService;
        self.SSConfirmationService = SSConfirmationService;
        self.SSAwsService = SSAwsService;

        self.data = SHOWSUITE.data;
        self.property = self.data.property;
        self.propertyId = self.data.property_id;
        self.EDITOR_API_KEY = self.data.EDITOR_API_KEY;

        self.updates = null;
        self.entry = null;
        self.modalOpen = false;
        self.editor = null;

        self.errTitle = false;
        self.errContent = false;
        self.errorMessage = null;

        if (self.SSUserService.hasPermissionForProperty('DMS_HOMESUITE_UPDATES_VIEW', self.propertyId)) {
            self.init();
        }
    }

    init() {
        const self = this;
        self.getUpdates();
    }

    /**
     * Loads the list of Updates for the current property. The server scopes
     * rigorously to this propertyId and to category 90, so the response
     * contains only this property's Homesuite Updates.
     */
    getUpdates() {
        const self = this;

        let url = 'sec/developer/properties/' + self.propertyId + '/homesuite-updates';

        loading(true);
        self.SSHttpService.getAPIRequest(url).then(function (response) {
            loading(false);

            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                self.updates = [];
                return;
            }
            self.SSAlertService.clear();
            self.updates = (response.data && response.data.updates) ? response.data.updates : [];
        });
    }

    toggleModal() {
        const self = this;
        self.modalOpen = !self.modalOpen;
        jQuery('#homesuiteUpdateModal').modal(self.modalOpen ? 'show' : 'hide');

        if (!self.modalOpen) {
            self.destroyFroala();
        }
    }

    /**
     * Opens the create or edit modal. The Description field is a Froala
     * rich-text editor — initialised here after the modal is in the DOM,
     * destroyed on close to avoid stale instances across opens.
     */
    openModal(entry) {
        const self = this;

        self.errTitle = false;
        self.errContent = false;
        self.errorMessage = null;

        self.entry = entry ? angular.copy(entry) : {};

        self.toggleModal();

        self.$timeout(function () {
            self.initFroala(self.entry.content || '');
        });
    }

    /**
     * Initialises (or re-initialises) the Froala editor bound to the
     * Description field. Uses Froala v4's `new FroalaEditor(...)` API
     * (the bundled vendor.js is v4.7.1 — the v2 jQuery plugin is gone).
     */
    initFroala(content) {
        const self = this;

        // Tear down any prior instance — opening the modal twice would
        // otherwise leave a stale editor attached.
        self.destroyFroala();

        self.editor = new FroalaEditor('#homesuiteUpdateFroala', {
            key:              self.EDITOR_API_KEY,
            placeholderText:  'Description',
            charCounterCount: true,
            charCounterMax:   500,
            heightMin:        200
        }, function () {
            // Set the initial HTML in the init callback so it is in place
            // by the time the user starts typing.
            self.editor.html.set(content || '');
        });

        // Modal stacks at z-index 10000 — keep toolbar/popups above it.
        jQuery('.fr-toolbar, .fr-tooltip, .fr-popup').css('z-index', '10001');
    }

    /** Destroys the Froala editor if it is currently attached. */
    destroyFroala() {
        const self = this;
        if (!self.editor) return;
        try { self.editor.destroy(); } catch (e) { /* already gone */ }
        self.editor = null;
    }

    /**
     * Validates and submits the form. On create, POSTs to the URL's
     * property; on edit, PUTs to the URL's property using the existing
     * row's update id. The server hard-codes category to 90 — callers
     * cannot punch into another CMS category from this surface.
     */
    save() {
        const self = this;

        // Pull the Froala HTML into the entry model before validating.
        if (self.editor) {
            try { self.entry.content = self.editor.html.get(true) || ''; }
            catch (e) { /* keep previous entry.content */ }
        }

        if (!self.validate()) return;

        let url = 'sec/developer/properties/' + self.propertyId + '/homesuite-updates';
        let isEdit = !!self.entry.id;
        if (isEdit) url += '/' + self.entry.id;

        let payload = {
            property_id: parseInt(self.propertyId, 10),
            title:       self.entry.title,
            content:     self.entry.content,
            file_uri:    self.entry.file_uri  || null,
            file_name:   self.entry.file_name || null,
            action_url:  self.entry.action_url || null,
            order:       self.entry.order || null
        };

        let httpCall = isEdit
            ? self.SSHttpService.putAPIRequest(url, payload)
            : self.SSHttpService.postAPIRequest(url, payload);

        loading(true);
        httpCall.then(function (response) {
            loading(false);

            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }

            self.SSAlertService.success(isEdit ? 'Update saved.' : 'Update created.');
            self.toggleModal();
            self.getUpdates();
        });
    }

    validate() {
        const self = this;

        // Froala wraps even empty content in <p><br></p>. Strip tags before
        // checking so an empty editor is treated as missing.
        const plainContent = (self.entry.content || '').replace(/<[^>]*>/g, '').trim();

        self.errTitle   = !self.entry.title || self.entry.title.trim().length === 0;
        self.errContent = plainContent.length === 0;

        return !(self.errTitle || self.errContent);
    }

    /**
     * Deletes the row after a confirm prompt. The server re-verifies that
     * the row belongs to this property AND to category 90, so cross-property
     * deletes are blocked even if the id is guessed.
     */
    remove(entry) {
        const self = this;

        self.SSConfirmationService.showConfirmation(
            'Delete this Update? This cannot be undone.',
            function () {
                let url = 'sec/developer/properties/' + self.propertyId + '/homesuite-updates/' + entry.id;

                loading(true);
                self.SSHttpService.deleteAPIRequest(url).then(function (response) {
                    loading(false);

                    if (response instanceof Error) {
                        self.SSAlertService.parseAndDisplayError(response);
                        return;
                    }

                    self.updates = self.updates.filter(function (u) { return u.id !== entry.id; });
                    self.SSAlertService.success('Update deleted.');
                });
            }
        );
    }

    /**
     * Uploads the selected file directly to S3 using the presigned policy
     * returned by the API. The server gates the policy endpoint by either
     * ADD or EDIT permission on this property — edit-only users can still
     * change the image on an existing Update.
     */
    upload(file) {
        const self = this;
        if (!file) return;

        if (!file.type || file.type.indexOf('image') === -1) {
            self.SSAlertService.danger('Error!', 'Please upload an image file only.');
            return;
        }

        let policyUrl = 'sec/developer/properties/' + self.propertyId + '/homesuite-updates/s3-policy-document';

        loading(true);
        self.SSAwsService.uploadAPIRequest(file, policyUrl, function (response) {
            loading(false);
            if (response instanceof Error) {
                self.SSAlertService.parseAndDisplayError(response);
                return;
            }
            self.entry.file_uri  = response.config.data.key;
            self.entry.file_name = response.config.data.randomname + '.' + response.config.data.extension;
        });
    }

    /** Clears the image from the entry — does NOT delete from S3. */
    removeImage() {
        const self = this;
        self.entry.file_uri  = null;
        self.entry.file_name = null;
    }

    /** Truncates a (possibly purified-HTML) description for the table view. */
    shortText(content, max) {
        if (!content) return '--';
        // Strip tags for the listing — full HTML still renders on the app side.
        let plain = String(content).replace(/<[^>]*>/g, '').trim();
        if (plain.length <= max) return plain;
        return plain.substring(0, max) + '…';
    }
}

PropertyKeyHandoverUpdatesController.$inject = [
    '$scope', '$timeout',
    'SSHttpService', 'SSAlertService', 'SSAppService',
    'SSUserService', 'SSUtilService', 'SSDateService',
    'SSConfirmationService', 'SSAwsService'
];

app.controller('PropertyKeyHandoverUpdatesController', PropertyKeyHandoverUpdatesController);
